import React, { useEffect } from 'react';
import {
  IResourceComponentsProps,
  useDelete,
  useGetIdentity,
  useNavigation,
  useNotification,
  usePermissions,
  useUpdate,
} from '@pankod/refine-core';
import {
  DataGrid,
  useDataGrid,
  GridColumns,
  GridActionsCellItem,
  List,
  Typography,
} from '@pankod/refine-mui';
import { Edit, Close } from '@mui/icons-material';
import { IGetOfferCodes } from 'interfaces';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import {
  collection,
  getDocs,
  doc,
  query,
  where,
  getDoc,
  writeBatch,
} from 'firebase/firestore';
import { fbFirestore } from 'lib/firebaseService';
import { OfferExpiryType, OfferPeriodType } from 'config/enum';
import { useRole } from 'hooks/auth/permissions/useRole';
import { useOfferLeft } from 'hooks/offers/useOfferLeft';

dayjs.extend(relativeTime);
dayjs.extend(duration);

export const OfferCodeList: React.FC<
  IResourceComponentsProps
> = () => {
  const notification = useNotification();
  const { push, edit } = useNavigation();
  const { mutateAsync: mutateDelete } = useDelete();
  const { mutate: mutateUpdate } = useUpdate();
  const { offerLeftText, reduceOfferLeft } = useOfferLeft();
  const { isAdmin } = useRole();
  const permissions = usePermissions();
  const currentUser = useGetIdentity();
  const initialFilter =
    permissions?.data?.role === 'manager'
      ? [
          {
            field: 'created_by_uid',
            operator: 'eq' as const,
            value: currentUser?.data?.uid,
          },
        ]
      : [];
  const { dataGridProps } = useDataGrid<IGetOfferCodes>({
    initialPageSize: 5,
    initialSorter: [
      {
        field: 'created_at',
        order: 'desc',
      },
    ],
    permanentFilter: initialFilter,
  });

  const resetDevice = async (row: IGetOfferCodes) => {
    if (row.device_using) {
      await mutateUpdate({
        resource: 'Offers',
        id: row.id,
        values: {
          device_using: null,
        },
      });
    } else {
      push(`/device_usings?code=${row.code}`);
    }
  };

  const deleteOffer = (row: IGetOfferCodes) => async () => {
    try {
      const offerRef = doc(collection(fbFirestore, 'Offers'), row.id);
      const offerDoc = await getDoc(offerRef);
      if (!offerDoc.exists()) {
        throw new Error('Offer not found');
      }
      const offer = offerDoc.data();

      await mutateDelete({
        resource: 'Offers',
        id: row.id,
        mutationMode: 'undoable',
      });

      const batch = writeBatch(fbFirestore);

      if (offer.use > 0) {
        const deviceUsingsQuery = query(
          collection(fbFirestore, 'device_usings'),
          where('code', '==', offer.code),
        );
        (await getDocs(deviceUsingsQuery)).forEach((doc) =>
          batch.delete(doc.ref),
        );
      }

      await batch.commit();
      reduceOfferLeft();

      notification?.open?.({
        type: 'success',
        message: 'Device deleted',
      });
    } catch (e) {
      notification?.open?.({
        type: 'error',
        message: (e as Error)?.message,
      });
    }
  };

  const columns = React.useMemo<GridColumns<IGetOfferCodes>>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
      },
      {
        field: 'code',
        headerName: 'Code',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
      },
      {
        field: 'limit',
        headerName: 'Limit',
        flex: 1,
        minWidth: 40,
        disableReorder: true,
        sortable: true,
        renderCell: function render({ row }) {
          return (
            <Typography>
              {row.device_using || !row.limit ? 1 : row.limit}
            </Typography>
          );
        },
      },
      {
        field: 'use',
        headerName: 'Use',
        flex: 1,
        minWidth: 40,
        disableReorder: true,
        sortable: true,
        renderCell: function render({ row }) {
          return (
            <Typography>
              {row.device_using ? 1 : row.use || 0}
            </Typography>
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Create Date',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
        renderCell: function render({ row }) {
          return (
            <Typography>
              {dayjs(row.created_at.toDate()).format(
                'YYYY-MM-DD HH:mm:ss',
              )}
            </Typography>
          );
        },
      },
      {
        field: 'Expiry date',
        headerName: 'Expiry on/after date',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
        renderCell: function render({ row }) {
          const expiryDate = (() => {
            switch (row.type) {
              case OfferExpiryType.ByPeriod:
                return dayjs
                  .duration({
                    [row.period_type || OfferPeriodType.Day]:
                      row.period,
                  })
                  .humanize();
              default:
                return dayjs(row.expiry_date.toDate()).format(
                  'YYYY-MM-DD HH:mm:ss',
                );
            }
          })();
          return <Typography>{expiryDate}</Typography>;
        },
      },
      {
        field: 'actions',
        headerName: '#',
        type: 'actions',
        width: 40,
        getActions: function render({ row }) {
          return [
            <GridActionsCellItem
              key={1}
              label="Reset Device"
              icon={<Edit color="success" />}
              onClick={() => resetDevice(row)}
              showInMenu
            />,
            <GridActionsCellItem
              key={1}
              label="Edit"
              icon={<Edit color="success" />}
              onClick={() => edit('Offers', row.id)}
              showInMenu
            />,
            <GridActionsCellItem
              key={2}
              label="Delete"
              icon={<Close color="error" />}
              onClick={deleteOffer(row)}
              showInMenu
            />,
          ];
        },
      },
    ],
    [],
  );

  return (
    <List
      title={`Offer Codes${offerLeftText}`}
      createButtonProps={{
        children: <> Create New Offer Code </>,
      }}
      canCreate
      cardProps={{ sx: { paddingX: { xs: 0, md: 0 } } }}
    >
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        disableColumnFilter={true}
        disableColumnSelector={true}
        rowsPerPageOptions={[5, 10, 15, 20]}
        density="comfortable"
        sx={{
          '& .MuiDataGrid-cell:hover': {
            cursor: 'pointer',
          },
        }}
        onRowClick={(row) => {
          edit('Offers', row.id);
        }}
        columnVisibilityModel={{
          limit: isAdmin,
        }}
      />
    </List>
  );
};
